// @ts-expect-error: @rails/request.js is not typed
import { FetchRequest } from '@rails/request.js'
import { useEffect, useState } from 'react'
import { type SubmitHandler, useForm } from 'react-hook-form'

import { useWindowLocation } from 'utils/windowLocation'

import { type Partner } from './types'

type ReferralPageDetails = {
  slug: string;
  agency_logo?: FileList | null;
  agent_photo?: FileList | null;
}

export default function ReferralPageDetailsForm ({
  partner,
  agencyLogoUrl,
  isInitialSetup = false,
  agentPhotoUrl,
}: {
  partner: Partner;
  isInitialSetup?: boolean;
  agencyLogoUrl?: string;
  agentPhotoUrl?: string;
}) {
  const { assign } = useWindowLocation()

  const [submissionError, setSubmissionError] = useState<string | undefined>()
  const [loading, setLoading] = useState(false)
  const [agencyLogoPreview, setAgencyLogoPreview] = useState<
    string | undefined
  >(agencyLogoUrl)
  const [agentPhotoPreview, setAgentPhotoPreview] = useState<
    string | undefined
  >(agentPhotoUrl)

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<ReferralPageDetails>({
    defaultValues: {
      slug: partner.slug,
    },
  })
  const agencyLogoFile = watch('agency_logo')
  const agentPhotoFile = watch('agent_photo')

  useEffect(() => {
    if (agencyLogoFile && agencyLogoFile.length > 0) {
      const reader = new FileReader()
      reader.onload = (event) => {
        setAgencyLogoPreview(event.target?.result as string)
      }
      reader.readAsDataURL(agencyLogoFile[0])
    } else {
      setAgencyLogoPreview(agencyLogoUrl)
    }
  }, [agencyLogoFile, agencyLogoUrl])

  useEffect(() => {
    if (agentPhotoFile && agentPhotoFile.length > 0) {
      const reader = new FileReader()
      reader.onload = (event) => {
        setAgentPhotoPreview(event.target?.result as string)
      }
      reader.readAsDataURL(agentPhotoFile[0])
    } else {
      setAgentPhotoPreview(agentPhotoUrl)
    }
  }, [agentPhotoFile, agentPhotoUrl])

  const onSubmit: SubmitHandler<ReferralPageDetails> = async (data) => {
    setLoading(true)

    const formData = new FormData()
    formData.append('slug', data.slug)

    if (data.agency_logo && data.agency_logo.length > 0) {
      formData.append('agency_logo', data.agency_logo[0])
    }
    if (data.agent_photo && data.agent_photo.length > 0) {
      formData.append('agent_photo', data.agent_photo[0])
    }
    formData.append(
      'is_initial_setup',
      isInitialSetup ? isInitialSetup.toString() : 'false'
    )

    try {
      // TODO: this method of making an API call is deprecated -- please use reactquery instead
    // see https://www.notion.so/thatchhealth/Using-React-Query-for-Fetching-Data-160114874bc280379918d43442405589
      const request = new FetchRequest(
        'put',
        '/api/broker/settings/referral_page_details',
        {
          headers: {
            Accept: 'application/json',
          },
          body: formData,
        }
      )

      const response = await request.perform()
      if (response.ok) {
        const data = await response.json
        assign(data.redirect_to)
      } else if (response.statusCode === 401) {
        setSubmissionError(
          'Your session expired. Please refresh the page and sign in again to continue.'
        )
      } else if (response.statusCode === 422) {
        const errorData = await response.json
        const errors = errorData.errors
        const partnerErrors = (errors.partner || []).join(', ')
        setSubmissionError(
          `There was an error updating referral page details. Please correct the errors and try again: ${partnerErrors}`
        )
      } else {
        const errorData = await response.json
        const errors = errorData.errors
        console.error('Error updating referral page details: ', errors)
        setSubmissionError(
          'There was an error updating referral page details. Please contact support.'
        )
      }
    } catch (error: any) {
      console.error('Error updating referral page details: ', error)
      setSubmissionError(
        'There was an error updating referral page details. Please contact support.'
      )
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <h2 className='h2 font-bold mb-5'>Referral Page</h2>
      <p className='text-sm text-gray-700 mb-4'>
        Customize the look and feel of your referral page page that clients
        can use to sign up.
      </p>
      {submissionError && (
        <div
          className='bg-red-100 border border-red-400 text-red-700 p-2 mb-4 rounded relative text-center text-xs'
          role='alert'
        >
          {submissionError}
        </div>
      )}
      <form
        className='flex flex-col justify-between gap-y-6 max-w-xl'
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className={errors.slug && 'field-with-errors'}>
          <label className='label-default font-medium mb-2' htmlFor='slug'>
            URL
          </label>

          <div className={`flex gap-1 items-center${errors.slug ? ' field-with-errors' : ''}`}>
            <p className='text-sm text-gray-700'>thatch.com/b/</p>
            <input
              id='slug'
              className='input-default'
              aria-invalid={errors.slug ? 'true' : 'false'}
              {...register('slug', {
                pattern: {
                  value: /^[a-zA-Z0-9_-]+$/,
                  message:
                    'Slug can only contain letters, numbers, hyphens, and underscores.',
                },
              })}
              autoComplete='off'
              disabled={loading}
              placeholder='acme-insurance-agency'
            />
          </div>
          {errors.slug && (
            <span className='field-error'>{errors.slug.message}</span>
          )}
        </div>

        <div>
          <label
            className='label-default font-medium mb-2'
            htmlFor='agency_logo'
          >
            Agency Logo (JPEG, TIFF, or PNG)
          </label>
          <div className='flex gap-4 items-center bg-white'>
            {agencyLogoPreview && (
              <img
                src={agencyLogoPreview}
                className='h-32 object-cover rounded-full w-32 flex-none'
                alt='Uploaded agency logo'
              />
            )}
            <input
              id='agency_logo'
              type='file'
              className='input-default'
              {...register('agency_logo')}
              accept='image/jpeg, image/tiff, image/png'
              disabled={loading}
            />
          </div>
        </div>

        <div>
          <label
            className='label-default font-medium mb-2'
            htmlFor='agent_photo'
          >
            Photo of yourself (JPEG, TIFF, or PNG)
          </label>
          <div className='flex gap-4 items-center bg-white'>
            {agentPhotoPreview && (
              <img
                src={agentPhotoPreview}
                className='h-32 object-cover rounded-full w-32 flex-none'
                alt='Uploaded agent photo'
              />
            )}
            <input
              id='agent_photo'
              type='file'
              className='input-default'
              {...register('agent_photo')}
              accept='image/jpeg, image/tiff, image/png'
              disabled={loading}
            />
          </div>
        </div>

        <div className='flex justify-end'>
          <button className='button w-auto' disabled={loading} type='submit'>
            Save
          </button>
        </div>
      </form>
    </>
  )
}
